import { Tabs, TabsList, TabsTrigger } from '@you/ui'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { useUserStore } from '@/stores/userStore'
import { WorkspaceType } from '@/api/types/workspace.types'
import { OrgTag } from './apps'

// isOrgOwner：当前空间是组织空间且当前用户是 owner
const useIsOrgOwner = () => {
  const { currentWorkspace } = useWorkspaceStore()
  const { currentUser } = useUserStore()
  return (
    currentWorkspace?.type === WorkspaceType.Organization &&
    String(currentWorkspace.userId) === String(currentUser?.userId)
  )
}

const BASE_TABS = [
  { value: 'member', label: '人员管理', path: '/workspace/member' },
  { value: 'setting', label: '设置', path: '/workspace/setting' },
] as const

const Workspace = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<string>('member')
  const { currentWorkspace } = useWorkspaceStore()
  const isOrgOwner = useIsOrgOwner()

  const TAB_CONFIG = isOrgOwner
    ? [
        ...BASE_TABS,
        { value: 'apps', label: '应用授权', path: '/workspace/apps' } as const,
      ]
    : BASE_TABS

  useEffect(() => {
    const currentPath = location.pathname
    const tab = TAB_CONFIG.find((t) => currentPath === t.path)
    if (tab) {
      setActiveTab(tab.value)
    } else if (currentPath === '/workspace') {
      navigate('/workspace/member', { replace: true })
    }
  }, [location.pathname, navigate, isOrgOwner])

  const handleTabChange = (value: string) => {
    const tab = TAB_CONFIG.find((t) => t.value === value)
    if (tab) {
      setActiveTab(value)
      navigate(tab.path)
    }
  }

  return (
    <div className="flex flex-col h-full">
      {currentWorkspace && (
        <div className="flex items-center gap-3 mb-4">
          <Avatar className="w-10 h-10">
            <AvatarImage
              src={currentWorkspace.icon}
              alt={currentWorkspace.name}
            />
            <AvatarFallback className="text-lg font-medium">
              {currentWorkspace.name.slice(0, 1)}
            </AvatarFallback>
          </Avatar>
          <div className="flex items-center gap-2">
            <span className="text-2xl font-semibold text-gray-900">
              {currentWorkspace.name}
            </span>
            {currentWorkspace.type === WorkspaceType.Organization && <OrgTag />}
          </div>
        </div>
      )}

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          {TAB_CONFIG.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className="text-base"
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-6">
        <Outlet />
      </div>
    </div>
  )
}

export default Workspace
